import { assign, find } from 'lodash';

import { straight as straightData } from '../lib/axel/model';
import { naturalSort, nullToBottomSort, numericalSort } from '../lib/axel/sorter';
import { AxelDate, AxelNumber, AxelString } from '../lib/axel/type';
import { orderedComparatorMixin } from '../lib/axel/util';
import { Dataset } from '@mode/legacy/data-access-model-js';
import { ReportTypes } from '@mode/shared/contract-common';
import { ColumnSettings } from '@mode/legacy/data-access-model-js';
import {
  MultiValueFilterControl,
  RangeValueFilterControl,
  LegacyReportFilterControl,
  SingleValueFilterControl,
} from '@mode/filters/util';
import TableDataService from '../services/table-data-service';

function getValue(data, colType) {
  const typeCasters = {
    'plain-text': (d) => new AxelString(d),
    date: (d) => new AxelDate(d),
    financial: (d) => new AxelNumber(d),
    currency: (d) => new AxelNumber(d),
    number: (d) => new AxelNumber(d),
    percentage: (d) => new AxelNumber(d),
  };

  return typeCasters[colType](data);
}

function columnDataType(dataset: Dataset, colKey) {
  if (dataset == null) {
    return null;
  }

  const targetColumn: any = find(dataset.columns, { name: colKey });

  return targetColumn ? targetColumn.type : null;
}

function sortTable(colTypeFn, colKey, order, aSortable, bSortable) {
  const a = aSortable.data;
  const b = bSortable.data;
  const epoch = (date) => (date.getTime ? date.getTime() : null);
  const columnType = colTypeFn(colKey);
  // curry mixin helper with nullToBottomSort comparator
  const nullSortMixin = (comparator) => orderedComparatorMixin(nullToBottomSort, comparator, order);
  const sorters = {
    'plain-text': () => nullSortMixin(naturalSort)(a.toStringOrNull(), b.toStringOrNull()),
    date: () => nullSortMixin(numericalSort)(epoch(a.valueOf()), epoch(b.valueOf())),
    number: () => nullSortMixin(numericalSort)(a.valueOrNull(), b.valueOrNull()),
    percentage: () => sorters.number(),
    financial: () => sorters.number(),
    currency: () => sorters.number(),
  };

  return (sorters[columnType] || sorters['plain-text'])();
}

function resolveColumnDataType(dataset: Dataset, colKey) {
  const DEFAULT_VALUE_TYPE = 'plain-text';
  const dataTypeMap = {
    number: 'number',
    integer: 'number',
    float: 'number',
    real: 'number',
    date: 'date',
    datetime: 'date',
  };
  const resolveSelectedTypeFromDataType = (colKey) => {
    const dataType = columnDataType(dataset, colKey);
    return dataTypeMap[dataType] || DEFAULT_VALUE_TYPE;
  };

  return resolveSelectedTypeFromDataType(colKey);
}

function columnType(columnSettings: ColumnSettings, colKey) {
  const colSettings = columnSettings && columnSettings[colKey];

  return colSettings && colSettings.hasOwnProperty('type') ? colSettings['type'] : null;
}

export function resolveColumnType(dataset: Dataset, settings: ColumnSettings, colKey: string) {
  const DEFAULT_VALUE_TYPE = 'plain-text';
  const availableTypes = ['plain-text', 'date', 'financial', 'currency', 'number', 'percentage'];
  const selectedColumnType = columnType(settings, colKey) || 'automatic';
  const resolveSelectedType = (type) => {
    if (availableTypes.indexOf(type) >= 0) {
      return type;
    } else {
      return DEFAULT_VALUE_TYPE;
    }
  };

  if (!selectedColumnType || selectedColumnType === 'automatic') {
    return resolveColumnDataType(dataset, colKey);
  } else {
    return resolveSelectedType(selectedColumnType);
  }
}

export function getDatasetModel(dataset: Dataset, opts) {
  const colTypeFn = resolveColumnDataType.bind(null, dataset);

  return straightData(
    dataset.content,
    assign(
      {},
      {
        value: (data, colKey) => () => getValue(data, resolveColumnDataType(dataset, colKey)),
        columns: dataset.columns.map((col) => col.name),
        sorter: sortTable.bind(null, colTypeFn),
        rowLimit: TableDataService.DISPLAY_ROW_LIMIT,
      },
      opts
    )
  );
}

export function buildReportFilterControls(
  filterControlRecords: ReportTypes.ReportFilterControl[]
): LegacyReportFilterControl[] {
  const filterControls = new Array<LegacyReportFilterControl>();

  for (const record of filterControlRecords) {
    switch (record.filterType) {
      case ReportTypes.ReportFilterType.Single:
        filterControls.push(SingleValueFilterControl.fromJS(record));
        break;
      case ReportTypes.ReportFilterType.Multi:
        filterControls.push(MultiValueFilterControl.fromJS(record));
        break;
      case ReportTypes.ReportFilterType.Range:
        filterControls.push(RangeValueFilterControl.fromJS(record));
        break;
    }
  }

  return filterControls;
}
